// Live portfolio-manager signal series + the "in position" rule for the
// asset-page indicator overlay. Kept separate from the (firebase-coupled)
// useIndicatorOverlay hook so this pure product rule can be unit-tested in
// isolation.
//
// The asset-page indicator shows the live portfolio manager's actual signal, so
// users see the same in/out state the fund trades on (not the display-only
// rotation indicator, whose producing pipeline is flaky and back-dates rows):
//   BTC page  -> sub7's strategy series (best BTC-cluster portfolio manager)
//   ETH page  -> sub2's strategy series (best ETH portfolio manager)
//
// SOURCE OF TRUTH: these names mirror each sub-account's
// PORTFOLIO_MANAGEMENT_STRATEGY in the bogg repo at
// deployments/prod/binance/management/manager-portfolio-binance-sub{7,2}.yaml.
// They are hardcoded (rather than discovered via listSeries) because they encode
// a deliberate "best portfolio manager per asset" product choice. If the fund
// reassigns which one is best, update these to match that deployment config.
export const BTC_PORTFOLIO_MANAGER_SIGNAL_SERIES =
    'strategy-service.clustering:agg_4_btc|screening:binance_selection_usdc_8:sharpe_ratio_14:top_1.crypto.strategy.86400000000000';
export const ETH_PORTFOLIO_MANAGER_SIGNAL_SERIES =
    'strategy-service.clustering:bis_k_means_6_eth|screening:binance_rule_usdc_v14:top_1.crypto.strategy.86400000000000';

// Fiat and stablecoins are cash, not a market position — a portfolio manager
// sitting in these is defensively "out", the same as an empty target_distribution.
const CASH_ASSETS = new Set([
    'usd', 'usdc', 'usdt', 'busd', 'fdusd', 'dai', 'tusd', 'usdp',
    'eur', 'chf', 'gbp', 'jpy',
]);

// 1 (green) when the portfolio manager holds a non-cash (crypto) position, else
// 0 (red). It is "in position" when target_distribution has positive weight in a
// non-cash asset; empty {} or fiat/stablecoin-only means it is in cash.
export function portfolioManagerInPosition(data: any): number {
    const td = data?.target_distribution ?? {};
    const cryptoWeight = Object.entries(td).reduce(
        (sum: number, [asset, weight]: [string, any]) =>
            CASH_ASSETS.has(String(asset).toLowerCase())
                ? sum
                : sum + (Number(weight) || 0),
        0
    );
    return cryptoWeight > 0 ? 1 : 0;
}

// Synthetic notification event ids for the per-asset portfolio-manager signal
// subscription. MUST match the indexer pm_signals source PM_SIGNALS[].event_id
// (signals/notifications/sources/pm_signals.py). Subscribing writes
// users/{uid}/subscriptions/<eventId>; the source emits alerts with the same
// event_id, so the existing event fan-out routes them to subscribers.
export const PM_SIGNAL_EVENT_ID: Record<'btc' | 'eth', string> = {
    btc: 'pmsignal_btc',
    eth: 'pmsignal_eth',
};

export interface PmSignalTarget {
    key: 'btc' | 'eth';
    eventId: string;
    title: string;
}

// Only BTC and ETH have a live portfolio manager to subscribe to; other assets
// return null (no signal notification).
export function pmSignalForTicker(
    ticker: string | null | undefined
): PmSignalTarget | null {
    const t = (ticker ?? '').toUpperCase();
    if (t === 'BTC') return { key: 'btc', eventId: PM_SIGNAL_EVENT_ID.btc, title: 'BTC signal' };
    if (t === 'ETH') return { key: 'eth', eventId: PM_SIGNAL_EVENT_ID.eth, title: 'ETH signal' };
    return null;
}

// Default series for the asset-page indicator overlay. These are the
// display-only rotation indicators, deliberately chosen as a neutral default
// that does not steer users into any one live portfolio manager — the dropdown
// shows each option's measured performance, so the choice is theirs.
//
// top_10 rather than top_20: it beat top_20 on both assets over the measured
// window (net Sharpe BTC -0.38 vs -0.44, ETH +0.25 vs +0.23) and matches the
// ETH speedometer, which already used top_10.
export const DEFAULT_INDICATOR_SERIES: Record<'btc' | 'eth', string> = {
    btc: 'strategy-service.rotation:ethereum:v1_btc_dist:sharpe_ratio_730:top_10.crypto.strategy.86400000000000',
    eth: 'strategy-service.rotation:ethereum:v1_eth_dist:sharpe_ratio_730:top_10.crypto.strategy.86400000000000',
};

// Series known to publish a continuous 0..1 dial rather than an in/out switch.
// The performance document reports this per series, but until it exists these
// two must not be guessed as binary: their weight is almost always above zero,
// so an in/out reading paints the band permanently green. Measured on the live
// data, they sit strictly between 0 and 1 on 210 and 236 of 398 days.
export const KNOWN_FRACTIONAL_SERIES: ReadonlySet<string> = new Set([
    DEFAULT_INDICATOR_SERIES.btc,
    DEFAULT_INDICATOR_SERIES.eth,
]);

// The catalog formatter renders these as "Ethereum : V1 Btc Dist : …", because
// `ethereum` is the producing config file (strategy_rotation/rotation_ethereum.yml),
// not the traded asset — which reads as an ETH indicator on the BTC page. Both
// are the mean of 10 BTC- resp. ETH-clusterings, so name them for what they are.
export const INDICATOR_DISPLAY_NAMES: Record<string, string> = {
    [DEFAULT_INDICATOR_SERIES.btc]: 'BTC Ensemble (10 clusterings)',
    [DEFAULT_INDICATOR_SERIES.eth]: 'ETH Ensemble (10 clusterings)',
};
